import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from './components/layout/AdminSidebar';
import AdminHeader from './components/layout/AdminHeader';
import OverviewPage from './components/pages/OverviewPage';
import UsersPage from './components/pages/UsersPage';
import FeedbackPage from './components/pages/FeedbackPage';
import DevIDsPage from './components/pages/DevIDsPage';
import useAdminData from './hooks/useAdminData';
import useIsAdmin from './hooks/useIsAdmin';

const ACCENT = '#CC1A00';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activePage,  setActivePage]  = useState('overview');
  const [excludeDevs, setExcludeDevs] = useState(false);
  const [devIds,      setDevIds]      = useState([]);
  const [collapsed,   setCollapsed]   = useState(false);

  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const { data, loading, error } = useAdminData();

  // Seed devIds from backend is_dev field (drives toggle UI in DevIDsPage only)
  useEffect(() => {
    if (data?.users) {
      setDevIds(data.users.filter(u => u.is_dev).map(u => u.user_id));
    }
  }, [data]);

  // Optimistic toggle for DevIDsPage UI; metrics come from the server
  const handleToggleDev = useCallback((userId, isDev) => {
    setDevIds(prev => isDev ? [...prev, userId] : prev.filter(d => d !== userId));
  }, []);

  // Redirect non-admins as soon as we know their status
  useEffect(() => {
    if (!adminLoading && isAdmin === false) {
      sessionStorage.setItem('navigation_in_progress', 'true');
      sessionStorage.setItem('app_initiated_navigation', 'true');
      sessionStorage.setItem('legitimate_navigation', 'true');
      navigate('/pnc', { replace: true });
      setTimeout(() => {
        sessionStorage.removeItem('navigation_in_progress');
        sessionStorage.removeItem('app_initiated_navigation');
      }, 300);
    }
  }, [isAdmin, adminLoading, navigate]);

  const currentUser = {
    name:  sessionStorage.getItem('username') || localStorage.getItem('username') || 'Admin',
    email: sessionStorage.getItem('email')    || localStorage.getItem('email')    || '',
  };

  const sidebarW = collapsed ? 64 : 248;
  const isUsers  = activePage === 'users';

  useEffect(() => {
    if (!document.getElementById('admin-inter-font')) {
      const link = document.createElement('link');
      link.id   = 'admin-inter-font';
      link.rel  = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  const handleBackToChat = () => {
    sessionStorage.setItem('navigation_in_progress', 'true');
    sessionStorage.setItem('app_initiated_navigation', 'true');
    sessionStorage.setItem('legitimate_navigation', 'true');
    navigate('/pnc');
    setTimeout(() => {
      sessionStorage.removeItem('navigation_in_progress');
      sessionStorage.removeItem('app_initiated_navigation');
    }, 300);
  };

  const handleLogout = () => {
    sessionStorage.setItem('navigation_in_progress', 'true');
    sessionStorage.setItem('app_initiated_navigation', 'true');
    sessionStorage.setItem('legitimate_navigation', 'true');
    localStorage.clear();
    sessionStorage.clear();
    window.location.replace('/login');
  };

  if (adminLoading || loading) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: '#F5F6FA', fontFamily: "'Inter', sans-serif" }}>
        <div style={{ textAlign: 'center', color: '#9CA3AF' }}>
          <div style={{ width: 40, height: 40, border: '3px solid #E5E7EB', borderTopColor: ACCENT, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          <div style={{ fontSize: 14 }}>Loading dashboard...</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: '#F5F6FA', fontFamily: "'Inter', sans-serif" }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>⚠</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#DC2626', marginBottom: 4 }}>Failed to load dashboard</div>
          <div style={{ fontSize: 13, color: '#6B7280' }}>{error}</div>
        </div>
      </div>
    );
  }

  // All metrics computed server-side; just pick the right set based on the toggle
  const activeMetrics = excludeDevs ? data.metrics_real : data.metrics_all;

  return (
    <div style={{
      display: 'flex', height: '100vh', overflow: 'hidden',
      background: '#F5F6FA',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <AdminSidebar
        activePage={activePage}
        onNavigate={setActivePage}
        collapsed={collapsed}
        accent={ACCENT}
        currentUser={currentUser}
      />

      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        marginLeft: sidebarW,
        transition: 'margin-left 0.22s cubic-bezier(.4,0,.2,1)',
        overflow: 'hidden', minWidth: 0,
      }}>
        <AdminHeader
          activePage={activePage}
          excludeDevs={excludeDevs}
          setExcludeDevs={setExcludeDevs}
          devCount={data.metrics_all.total_users - data.metrics_real.total_users}
          onToggleSidebar={() => setCollapsed(c => !c)}
          accent={ACCENT}
          onLogout={handleLogout}
          onBackToChat={handleBackToChat}
        />

        <main style={{
          flex: 1,
          overflow:  isUsers ? 'hidden' : 'auto',
          padding:   isUsers ? 0 : '22px',
          display:   isUsers ? 'flex' : 'block',
          minHeight: 0,
        }}>
          {activePage === 'overview' && (
            <OverviewPage
              users={data.users}
              metrics={activeMetrics}
              thumbs={activeMetrics.thumbs}
              monthLabels={data.monthly_labels}
              monthlyQueries={excludeDevs ? data.monthly_queries_real : data.monthly_queries_all}
              cumulativeUsers={excludeDevs ? data.cumulative_users_real : data.cumulative_users_all}
              excludeDevs={excludeDevs}
              devIds={devIds}
              accent={ACCENT}
            />
          )}
          {activePage === 'users' && (
            <UsersPage
              users={data.users}
              devIds={devIds}
              excludeDevs={excludeDevs}
              accent={ACCENT}
            />
          )}
          {activePage === 'feedback' && (
            <FeedbackPage
              feedbackEntries={data.feedback_entries}
              devIds={devIds}
              excludeDevs={excludeDevs}
              accent={ACCENT}
            />
          )}
          {activePage === 'devids' && (
            <DevIDsPage
              users={data.users}
              devIds={devIds}
              onToggleDev={handleToggleDev}
              excludeDevs={excludeDevs}
              setExcludeDevs={setExcludeDevs}
              metricsAll={data.metrics_all}
              metricsReal={data.metrics_real}
              accent={ACCENT}
            />
          )}
        </main>
      </div>
    </div>
  );
}
